import { Icon, Text } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import {
  DROPDOWN_MENU_PARTS,
  type MenuParts,
} from "#app/components/menus/menu-parts";
import { copyText, openHref } from "#app/lib/utils/copy-actions";

/**
 * THE dashboard's action roster — what a dashboard row answers with wherever
 * it carries a menu: the directory table's right-click and kebab, and the
 * module menu's Favorites/Recent rows (⋯ and right-click). One roster, so a
 * dashboard offers the same actions in the same order and dress everywhere.
 * The grouping is the basic entity roster's (`renderBasicEntityMenuItems`),
 * with the dashboard's own writes seated in the link group:
 *
 *   Favorite / Unfavorite
 *   ─────────
 *   Open in new tab · Copy link · Rename · Duplicate
 *   ─────────
 *   Delete (destructive)
 *   [ Sharing & Permissions ]   (workspace dashboards only)
 *
 * Rows the viewer cannot use are DISABLED with a reason, never hidden (the
 * view-tab menu's house rule) — except Sharing, which is absent on a
 * workflow dashboard because a workflow view has no share list of its own
 * (the workflow's is not the view's). Sharing is the menu's promoted foot
 * button: it is the one action that opens a whole dialog of its own.
 */
export type DashboardMenuItemsProps = Omittable<{
  /** Menu part set of the hosting menu kind. Defaults to the dropdown parts. */
  parts?: MenuParts;
  /** The dashboard's shareable URL — what Open in new tab opens and Copy
   *  link copies. */
  href: string;
  isFavorited: boolean;
  onFavorite: () => void;
  /** Why Rename and Duplicate are unavailable; null when the viewer may. */
  manageDisabledReason: string | null;
  onRename: () => void;
  onDuplicate: () => void;
  /** Why Delete is unavailable (no permission, or the scope's default view);
   *  null when the viewer may. */
  deleteDisabledReason: string | null;
  onDelete: () => void;
  /** Omitted on a workflow dashboard — the button is absent, not disabled. */
  onShare?: () => void;
  /** Whether the viewer may manage the dashboard's sharing. */
  canShare?: boolean;
}>;

export function renderDashboardMenuItems({
  parts = DROPDOWN_MENU_PARTS,
  href,
  isFavorited,
  onFavorite,
  manageDisabledReason,
  onRename,
  onDuplicate,
  deleteDisabledReason,
  onDelete,
  onShare,
  canShare = false,
}: DashboardMenuItemsProps) {
  const { Item, Separator, ButtonItem } = parts;
  const canManage = manageDisabledReason == null;

  return (
    <>
      <Item onClick={onFavorite}>
        <Icon
          color="muted"
          name={isFavorited ? "star-filled" : "star"}
          size="md"
        />
        <Text size="sm">{isFavorited ? "Unfavorite" : "Favorite"}</Text>
      </Item>
      <Separator />
      <Item onClick={() => openHref(href)}>
        <Icon color="muted" name="external-link" size="md" />
        <Text size="sm">Open in new tab</Text>
      </Item>
      <Item onClick={() => void copyText(href, "Link")}>
        <Icon color="muted" name="link" size="md" />
        <Text size="sm">Copy link</Text>
      </Item>
      <Item
        disabled={!canManage}
        {...(manageDisabledReason ? { disabledReason: manageDisabledReason } : {})}
        onClick={onRename}
      >
        <Icon color="muted" name="pencil" size="md" />
        <Text size="sm">Rename</Text>
      </Item>
      <Item
        disabled={!canManage}
        {...(manageDisabledReason ? { disabledReason: manageDisabledReason } : {})}
        onClick={onDuplicate}
      >
        <Icon color="muted" name="copy" size="md" />
        <Text size="sm">Duplicate</Text>
      </Item>
      <Separator />
      <Item
        disabled={deleteDisabledReason != null}
        {...(deleteDisabledReason ? { disabledReason: deleteDisabledReason } : {})}
        onClick={onDelete}
        variant="destructive"
      >
        <Icon name="trash" size="md" />
        <Text size="sm">Delete</Text>
      </Item>
      {onShare ? (
        <>
          <Separator />
          <ButtonItem disabled={!canShare} onClick={onShare}>
            Sharing &amp; Permissions
          </ButtonItem>
        </>
      ) : null}
    </>
  );
}
